import type {
  TChangeNeighborhoodPropertyDestination,
  TMajorActionChangeNeighborhoodData,
} from '@kapitalist/contract/types'
import {skins} from '~/game/skins'
import {cardFactory} from '~/models/game/factories'
import {PlayerGamePosition} from '~/models/game/GamePosition'
import type {TMajorActionUserFacingDescriptionData} from '~/models/game/MajorAction'
import MajorAction from '~/models/game/MajorAction'
import type {AnySpecialPropertyCard} from '~/models/game/types'
import {standardPNForIdThrows} from '~/models/game/utils'
import {mutateIf} from '~/utils/etc'
import {inTenseSkinVerb, sentenceCase} from '~/utils/language'

export default class MajorActionChangeNeighborhood extends MajorAction<TMajorActionChangeNeighborhoodData> {
  //   Standard properties never change neighborhoods except forcibly (i.e. the server merges your
  //   neighborhoods for you after some other action because the rules require it).
  card: AnySpecialPropertyCard
  destination: TChangeNeighborhoodPropertyDestination

  constructor(data: TMajorActionChangeNeighborhoodData) {
    super(data)
    this.card = cardFactory(data.card)
    this.destination = data.destination
  }

  // The neighborhood this move targets, or undefined when it starts a new set instead.
  get portfolioNeighborhoodId(): string | undefined {
    return this.destination.kind === 'existingNeighborhood'
      ? this.destination.portfolioNeighborhoodId
      : undefined
  }

  public userFacingDescription({
    tense,
    skin,
    gamePosition,
  }: TMajorActionUserFacingDescriptionData): string {
    const currentPlayer =
      gamePosition instanceof PlayerGamePosition
        ? gamePosition.currentPlayer
        : undefined
    const majorActor = gamePosition.majorActorPlayer
    const dropFirstNoun = tense === 'imperativePrompt'

    const destinationColor =
      this.destination.kind === 'existingNeighborhood'
        ? standardPNForIdThrows(majorActor.portfolio)(
            this.destination.portfolioNeighborhoodId
          ).displayColorShort(skin)
        : skins[skin].shortNeighborhoodNames[this.destination.color]

    return skins[skin].gameLogDescriptions.majorActions.changeNeighborhood({
      tense,
      actor: dropFirstNoun
        ? undefined
        : currentPlayer?.id === majorActor.id
          ? 'You'
          : majorActor.username,
      rotateVerb: mutateIf(
        inTenseSkinVerb(tense)(skins[skin].vocab.verbs.rotateDual),
        dropFirstNoun,
        sentenceCase
      ),
      cardName: this.card.displayName(skin),
      destinationColor,
    })
  }
}
